"use client";

import { memo } from "react";
import {
  Label,
  PolarGrid,
  PolarRadiusAxis,
  RadialBar,
  RadialBarChart,
} from "recharts";
import { ChartContainer, type ChartConfig } from "@/components/ui/chart";
import { cn } from "@/src/lib/utils";
import { getRiskLevelFromScore, getRiskLevelColor } from "../../constants/riskConfig";
import { SparkleIcon } from "@/components/icons/generated/SparkleIcon";

interface TrustIndexSegment {
  value: number;
  label?: string;
}

interface TrustIndexCircleProps {
  segments: TrustIndexSegment[];
  totalValue?: number;
  totalLabel?: string;
  title?: string;
  size?: number;
  autoFit?: boolean;
  primaryColor?: string;
  strokeWidth?: number;
  className?: string;
  hasTrustScore?: boolean;
  /** Показывать спаркл-иконку (только examru). */
  showSparkle?: boolean;
}


const TrustIndexCircle = memo(function TrustIndexCircle({
  totalValue,
  totalLabel = "балла",
  title = "Credibility",
  size,
  autoFit = false,
  primaryColor,
  className,
  hasTrustScore = false,
  showSparkle = false,
}: TrustIndexCircleProps) {
  if (typeof totalValue !== "number") return null

  const displayValue = totalValue < 0 ? 0 : totalValue;
  const isNegativeValue = totalValue < 0;

  const shouldAutoFit = autoFit || size === undefined;

  const riskLevel = getRiskLevelFromScore(displayValue);
  const riskColor = getRiskLevelColor(riskLevel);

  const color = hasTrustScore ? (primaryColor || riskColor) : "#e5e7eb";

  const chartData = [
    {
      name: "credibility",
      value: hasTrustScore ? displayValue : 100,
      fill: color
    },
  ];

  const chartConfig = {
    value: {
      label: totalLabel,
    },
    credibility: {
      label: title,
      color: color,
    },
  } satisfies ChartConfig;

  const maxValue = 100;
  const startAngle = 90;
  const endAngle = hasTrustScore ? 90 - (displayValue / maxValue) * 360 : 90;

  return (
    <div
      className={cn(
        "relative inline-flex items-center justify-center",
        shouldAutoFit && "w-full aspect-square",
        className
      )}
      style={shouldAutoFit ? { aspectRatio: '1 / 1' } : undefined}
    >
      {hasTrustScore && showSparkle && (
        <span className="absolute top-0 right-0 z-10 pointer-events-none text-[#B20BFF]">
          <SparkleIcon size={20} />
        </span>
      )}
      <ChartContainer
        config={chartConfig}
        className={cn(
          "mx-auto aspect-square max-w-full max-h-full",
          shouldAutoFit ? "w-full h-full" : ""
        )}
        style={shouldAutoFit ? undefined : { width: size, height: size }}
      >
        <RadialBarChart
          data={chartData}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={70}
          outerRadius={80}
        >
          <PolarGrid
            gridType="circle"
            radialLines={false}
            stroke="none"
            className="first:fill-muted last:fill-background"
            polarRadius={[76, 64]}
          />
          <RadialBar
            dataKey="value"
            background
            cornerRadius={10}
          />
          <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
            <Label
              content={({ viewBox }) => {
                if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                  return (
                    <text
                      x={viewBox.cx}
                      y={viewBox.cy}
                      textAnchor="middle"
                      dominantBaseline="middle"
                    >
                      {title && (
                        <tspan
                          x={viewBox.cx}
                          y={(viewBox.cy || 0) - 25}
                          className="fill-muted-foreground"
                          style={{
                            fontSize: '11px',
                            fill: '#9CA3AF',
                          }}
                        >
                          {title}
                        </tspan>
                      )}
                      <tspan
                        x={viewBox.cx}
                        y={(viewBox.cy || 0) + 5}
                        className="fill-foreground"
                        style={
                          isNegativeValue
                            ? {
                                fontSize: "32px",
                                fontWeight: 700,
                                fontFamily: "var(--font-monda)",
                              }
                            : {
                                fontSize: "32px",
                                fontWeight: 700,
                                fill: hasTrustScore ? color : riskColor,
                                fontFamily: "var(--font-monda)",
                              }
                        }
                      >
                        {isNegativeValue ? "—" : totalValue.toLocaleString()}
                      </tspan>
                    </text>
                  );
                }
              }}
            />
          </PolarRadiusAxis>
        </RadialBarChart>
      </ChartContainer>
    </div>
  );
});

export default TrustIndexCircle;
export type { TrustIndexCircleProps, TrustIndexSegment };
